import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  resetExplorerToInitialState,
  setFiles,
  setFolders,
  updateFolderAction,
} from "../state/explorer/explorerSlice";
import { ActionId, MenuAction, MenuContext } from "../components/menu/menus";
import {
  openDeleteModal,
  openManageAccessModal,
  openRemoteControlConnectionModal,
  openSetDateModal,
  openShareModal,
} from "../components/modal/confirm";
import { getContentType } from "../components/loader/ContentTypeUtils";
import { socket } from "./socketService";
import { useAuth } from "./useAuth";
import { FileDto, FolderDto } from "@braoshzmvavz/picnube-common";

export const useExplorerActions = (props: { demo?: boolean }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const explorerState = useAppSelector((state) => state.explorer);

  const handleSharePhoto = async (photoUrl: string, photoName: string) => {
    try {
      const response = await fetch(photoUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const file = new File([blob], photoName, {
        type: getContentType(photoName),
      });

      if (navigator.share) {
        await navigator.share({
          title: "Check out this photo!",
          text: "I wanted to share this photo with you.",
          files: [file],
        });
        console.log("Photo shared successfully!");
      } else {
        console.error("Web Share API not supported on this browser.");
        alert("Sharing is not supported in this browser.");
      }
    } catch (error) {
      console.error("Error sharing photo:", error);
    }
  };

  const handleDownloadPhoto = async (photoUrl: string, photoName: string) => {
    try {
      const response = await fetch(photoUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = photoName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      console.log("Photo downloaded successfully!");
    } catch (error) {
      console.error("Error downloading photo:", error);
    }
  };

  const handleDeleteFile = async (
    file: FileDto,
    folderId: string,
    deleteFileFn: (id: string) => Promise<any>
  ) => {
    await deleteFileFn(file.id);
    notifications.show({
      id: "file-deleted",
      color: "green",
      title: "File deleted succesfully",
      message: "The file and its contents have been deleted",
      autoClose: false,
    });

    if (file.folderId === folderId) {
      if (!file.folderId) {
        dispatch(resetExplorerToInitialState());
        navigate(props.demo ? "/demo/browse" : "/browse");
      } else {
        navigate(props.demo ? "/demo/browse" : "/browse/" + file.folderId);
      }
    } else {
      dispatch(setFiles(explorerState.files.filter((f: FileDto) => f.id !== file.id)));
    }
  };

  const handleDeleteFolder = async (
    folder: FolderDto,
    folderId: string,
    deleteFolderFn: (id: string) => Promise<any>
  ) => {
    await deleteFolderFn(folder.id);
    notifications.show({
      id: "folder-deleted",
      color: "green",
      title: "Folder deleted succesfully",
      message: "The folder and its contents have been deleted",
      autoClose: false,
    });

    if (folder.id === folderId) {
      if (!folder.parentId) {
        dispatch(resetExplorerToInitialState());
        navigate(props.demo ? "/demo/browse" : "/browse");
      } else {
        navigate(props.demo ? "/demo/browse" : "/browse" + folder.parentId);
      }
    } else {
      dispatch(setFolders(explorerState.folders.filter((f: FolderDto) => f.id !== folder.id)));
    }
  };

  const handleRemoteControlSlideshow = (
    ctx: MenuContext,
    isMobile: boolean,
    setOpenLightboxAndPlayFromFile: (state: any) => void
  ) => {
    const remoteControlToken = crypto.randomUUID();

    openRemoteControlConnectionModal(isMobile, remoteControlToken, async () => {
      const token = await auth.currentUser.getIdToken();

      socket.emit("frontend-a-init", {
        token: token,
        rcTocken: remoteControlToken,
      });

      if (ctx.file) {
        setOpenLightboxAndPlayFromFile({
          file: ctx.file,
          remoteControl: true,
        });
      }
      if (ctx.folder) {
        setOpenLightboxAndPlayFromFile({ position: 0, remoteControl: true });
      }
    });
  };

  const onActionClick = (
    actionConfig: MenuAction,
    ctx: MenuContext,
    {
      folderId,
      isMobile,
      openRef,
      setViewDetailFile,
      openFileDetail,
      setFolderToEdit,
      openEditFolderModal,
      openNewFolderModal,
      setOpenLightboxAndPlayFromFile,
      getSpaceAccessInfo,
      updateSpaceAccess,
      deleteFile,
      deleteFolder,
    }: {
      folderId: string;
      isMobile: boolean;
      openRef: React.RefObject<() => void>;
      setViewDetailFile: (file: FileDto) => void;
      openFileDetail: () => void;
      setFolderToEdit: (folder: FolderDto) => void;
      openEditFolderModal: () => void;
      openNewFolderModal: () => void;
      setOpenLightboxAndPlayFromFile: (state: any) => void;
      getSpaceAccessInfo: (folderId: string) => Promise<any>;
      updateSpaceAccess: (folderId: string, request: any) => Promise<any>;
      deleteFile: (id: string) => Promise<any>;
      deleteFolder: (id: string) => Promise<any>;
    }
  ) => {
    console.log("ExplorerActions.onActionClick", actionConfig, ctx);

    switch (actionConfig.id) {
      case ActionId.SHARE:
        if (!ctx.folder) return;
        openShareModal(ctx.folder, () => getSpaceAccessInfo(ctx.folder.id), (request) => updateSpaceAccess(ctx.folder.id, request), isMobile);
        break;

      case ActionId.SHARE_FILE:
        if (!ctx.file) return;
        handleSharePhoto(ctx.file.url, ctx.file.name);
        break;

      case ActionId.OPEN_FILE_DETAIL:
        if (!ctx.file) return;
        setViewDetailFile(ctx.file);
        openFileDetail();
        break;

      case ActionId.DOWNLOAD_FILE:
        if (!ctx.file) return;
        handleDownloadPhoto(ctx.file.url, ctx.file.name);
        break;

      case ActionId.MANAGE_ACCESS:
        if (!ctx.folder) return;
        openManageAccessModal(ctx.folder, () => getSpaceAccessInfo(ctx.folder.id), (request) => updateSpaceAccess(ctx.folder.id, request), isMobile);
        break;

      case ActionId.UPLOAD_FILES:
        openRef.current?.();
        break;

      case ActionId.DELETE_FILE:
        if (!ctx.file) return;
        handleDeleteFile(ctx.file, folderId, deleteFile);
        break;

      case ActionId.VIEW_GALLERY:
        if (!ctx.folder) return;
        navigate(`/gallery?folder=${ctx.folder.id}`);
        break;

      case ActionId.UPDATE_FOLDER:
        if (!ctx.folder) return;
        setFolderToEdit(ctx.folder);
        openEditFolderModal();
        break;

      case ActionId.DELETE_FOLDER:
        if (!ctx.folder) return;
        openDeleteModal(
          isMobile,
          "Delete folder",
          "Are you sure you want to delete this folder?",
          "This action is irreversible and cannot be undone. You will lose all files and subfolders inside this folder.",
          "Delete folder",
          () => handleDeleteFolder(ctx.folder, folderId, deleteFolder)
        );
        break;

      case ActionId.CREATE_FOLDER:
        openNewFolderModal();
        break;

      case ActionId.PLAY:
        if (ctx.file) {
          setOpenLightboxAndPlayFromFile({
            file: ctx.file,
            remoteControl: false,
          });
        }
        if (ctx.folder) {
          setOpenLightboxAndPlayFromFile({ position: 0, remoteControl: false });
        }
        break;

      case ActionId.REMOTE_CONTROL:
        if (!ctx.folder) return;
        handleRemoteControlSlideshow(ctx, isMobile, setOpenLightboxAndPlayFromFile);
        break;
    }
  };

  return {
    onActionClick,
  };
}; 